import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyAPI } from '../../services/api';
import { useBranchFilter } from '../../contexts/BranchFilterContext';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import { generatePropertyPDF } from '../../utils/pdfGenerator';
import './PropertyList.css';

const PropertyList = () => {
  const { t } = useTranslation();
  const { selectedBranchId } = useBranchFilter();

  // Helper function to get status string
  const getStatusString = (status) => {
    if (!status) return '-';
    const statusNum = typeof status === 'number' ? status : parseInt(status);
    switch (statusNum) {
      case 1:
        return t('property.create.fields.tab3.options.rent');
      case 2:
        return t('property.create.fields.tab3.options.booked');
      case 3:
        return t('property.create.fields.tab3.options.available');
      default:
        return '-';
    }
  };
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [printingPropertyId, setPrintingPropertyId] = useState(null);

  useEffect(() => {
    loadProperties();
  }, [page, selectedBranchId]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await propertyAPI.getAll(searchTerm, page, pageSize, selectedBranchId);
      setProperties(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      setErrorMessage(t('property.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadProperties();
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await propertyAPI.delete(id);
        setSuccessMessage(t('property.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadProperties();
      } catch (err) {
        setErrorMessage(t('property.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('property.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('property.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await propertyAPI.bulkDelete(selectedIds);
        setSuccessMessage(response.data.message || t('property.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadProperties();
      } catch (err) {
        setErrorMessage(err.response?.data?.message || t('property.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('property.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(properties.map(p => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handlePrint = async (property) => {
    try {
      setPrintingPropertyId(property.id);
      // Fetch full print-ready details
      const response = await propertyAPI.getForPrint(property.id);
      const propertyDetails = response.data;
      await generatePropertyPDF(property, propertyDetails);
      setSuccessMessage(t('property.list.messages.pdfSuccess'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setErrorMessage(t('property.list.messages.pdfError'));
      setShowErrorModal(true);
    } finally {
      setPrintingPropertyId(null);
    }
  };

  const handleExport = async (type) => {
    try {
      setShowExportMenu(false);
      let response;
      let filename;
      let contentType;

      switch (type) {
        case 'excel':
          response = await propertyAPI.exportExcel();
          filename = `Properties_${new Date().toISOString().split('T')[0]}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      saveAs(blob, filename);
      setSuccessMessage(t('property.list.messages.exportSuccess', { format: type.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      setErrorMessage(t('property.list.messages.exportError', { format: type.toUpperCase() }));
      setShowErrorModal(true);
    }
  };

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction();
    }
    setShowConfirmModal(false);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
        <div className="page-actions-left">
          <Link to="/property/create" className="btn-add">
            + {t('property.list.addNew')}
          </Link>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder={t('property.list.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
            />
            <button className="search-icon" onClick={handleSearch}>
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>
        <div className="action-buttons">
          <div className="export-menu-container">
            <button className="action-btn action-btn-export" title={t('common.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
              <i className="fas fa-file-export"></i>
            </button>
            {showExportMenu && (
              <div className="export-menu">
                <button onClick={() => handleExport('excel')}>
                  <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                </button>
              </div>
            )}
          </div>
          <button className="action-btn action-btn-delete" title={t('property.list.bulkDelete')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedIds.length === properties.length && properties.length > 0}
                />
              </th>
              <th>{t('property.list.columns.id')}</th>
              <th>{t('property.list.columns.code')}</th>
              <th>{t('property.list.columns.nameEng')}</th>
              <th>{t('property.list.columns.nameAr')}</th>
              <th>{t('property.list.columns.type')}</th>
              <th>{t('property.list.columns.city')}</th>
              <th>{t('property.list.columns.status')}</th>
              <th>{t('property.list.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {properties.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-4">
                  {t('property.list.messages.noData')}
                </td>
              </tr>
            ) : (
              properties.map((property) => (
                <tr key={property.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(property.id)}
                      onChange={() => handleSelectOne(property.id)}
                    />
                  </td>
                  <td>{property.id}</td>
                  <td>{property.code}</td>
                  <td>{property.nameEng}</td>
                  <td>{property.nameAr}</td>
                  <td>{property.realEstateType?.nameEng || property.RealEstateType?.NameEng || property.realEstateUsage?.nameEng || property.RealEstateUsage?.NameEng || '-'}</td>
                  <td>{property.city?.cityNameEng || property.City?.CityNameEng || property.placeTypeCity?.cityNameEng || property.PlaceTypeCity?.CityNameEng || '-'}</td>
                  <td>
                    <span className="status-badge">
                      {getStatusString(property.realEstateStatus || property.RealEstateStatus)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-cell">
                      <Link
                        to={`/property/view/${property.id}`}
                        className="btn btn-sm btn-view"
                        title={t('common.view')}
                      >
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link
                        to={`/property/edit/${property.id}`}
                        className="btn btn-sm btn-edit"
                        title={t('common.edit')}
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button
                        className="btn btn-sm btn-print"
                        onClick={() => handlePrint(property)}
                        title={t('common.print')}
                        disabled={printingPropertyId === property.id}
                      >
                        <i className={`fas ${printingPropertyId === property.id ? 'fa-spinner fa-spin' : 'fa-print'}`}></i>
                      </button>
                      <button
                        className="btn btn-sm btn-delete"
                        onClick={() => handleDelete(property.id)}
                        title={t('common.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <ul className="pagination">
            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                {t('common.previous')}
              </button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPage(i + 1)}>
                  {i + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                {t('common.next')}
              </button>
            </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyList;

